import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import {
  OfficeBuildingIcon,
  UserIcon,
  UsersIcon,
  ClipboardListIcon,
  ClipboardIcon,
  CogIcon,
  LogoutIcon,
  // UserGroupIcon,
} from "@heroicons/react/outline";
import { MenuIcon } from "@heroicons/react/outline";
import { useAuth0 } from "@auth0/auth0-react";

import { Modal } from "@tyrannosaurustech/ui";
import { Logo } from "../atoms/Logo";
import { FocusOutlineButton } from "../atoms/Button";
import { Sheet } from "../molecules/Sheet";
import { Theme } from "../atoms/theme";
import { InitialsAvatar } from "../atoms/Avatar";
import { AmbulanceIcon } from "../assets/AmbulanceIcon";
import { useDispatch } from "react-redux";
import { logout as logoutAction } from "../../redux/reducers/auth";
import { useUser } from "../../hooks/useLogin";
import { Link } from "react-router-dom";
import { checkIsCompleteProfileScreen } from "../../screens/CompleteProfile";

const NavWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.neutrals_500};
  box-shadow: ${({ theme }) => theme.shadows.dropshadow_m};
  padding: 1rem;
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;
const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 0.5rem;
`;
const FlexStartContentContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 0.75rem;
`;
const LinkWrapper = styled(NavLink)`
  text-decoration: none;
  color: black;
`;
const LinkSpacing = styled.div`
  margin-right: 2.5rem;
  @media (max-width: 1200px) {
    margin-right: 1rem;
  }
  @media (max-width: 768px) {
    margin-right: 0.5rem;
  }
`;
const DisplayFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const DesktopOnly = styled.div`
  display: flex;
  @media (max-width: 955px) {
    display: none;
  }
`;
const MobileOnly = styled.div`
  display: none;
  @media (max-width: 955px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;
const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 200ms ease-in-out;
  z-index: 998;
  &.open {
    opacity: 1;
  }
`;
const MobileMenuSheet = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 18.75rem;
  background: ${({ theme }) => theme.colors.neutrals_500};
  box-shadow: ${({ theme }) => theme.shadows.dropshadow_l};
  z-index: 999;
  padding: 1rem;
  transform: translateX(100%);
  transition: transform 200ms ease-in-out;
  &.open {
    transform: translateX(0);
  }
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const AbsoluteWrapper = styled.div`
  position: absolute;
  right: 0;
  margin-top: 1rem;
  z-index: 99;
`;

const adminLinks = [
  {
    icon: ClipboardIcon,
    text: "requests",
    path: "/admin/requests",
  },
  {
    icon: AmbulanceIcon,
    text: "medical Providers",
    path: "/admin/medical-providers",
  },
  {
    icon: OfficeBuildingIcon,
    text: "law firms",
    path: "/admin/law-firms",
  },
  {
    icon: UserIcon,
    text: "patients",
    path: "/admin/patients",
  },
  // {
  //   icon: UserGroupIcon,
  //   text: "filevine contacts",
  //   path: "/admin/filevine-contacts",
  // },
  // {
  //   icon: UsersIcon,
  //   text: "users",
  //   path: "/admin/users",
  // },
];

const clientLinks = (firmId) => {
  return [
    {
      icon: ClipboardListIcon,
      text: "Requests",
      path: `/client/${firmId}/requests`,
    },
    {
      icon: UsersIcon,
      text: "Users",
      path: `/client/${firmId}/users`,
    },
  ];
};

export const NavBar = () => {
  let navList;
  let dropOptions;
  const [openDrop, setOpenDrop] = useState(false);
  const [openNav, setOpenNav] = useState(false);
  const [navMounted, setNavMounted] = useState(false);

  useEffect(() => {
    if (openNav) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [openNav]);
  const [isOpen, setIsOpen] = useState(false);
  const { logout: auth0Logout } = useAuth0();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const user = useUser();

  const logout = () => {
    dispatch(logoutAction());
    auth0Logout({ returnTo: window.location.origin });
  };

  const adminOptions = [
    {
      text: "admins",
      action: () => {
        navigate("/admin/settings");
        setOpenDrop(false);
      },
    },
    {
      text: "audit logs",
      action: () => {
        navigate("/admin/audit-logs");
        setOpenDrop(false);
      },
    },
    {
      text: "logout",
      action: () => {
        setIsOpen(true);
        setOpenDrop(false);
      },
    },
  ];

  const clientOptions = [
    {
      text: "Feedback/Questions",
      action: () => {
        window.open("https://raynacorp.com/contact-us/");
        setOpenDrop(false);
      },
    },
    {
      text: "logout",
      action: () => {
        setIsOpen(true);
        setOpenDrop(false);
      },
    },
  ];

  const isCompleteProfileScreen = checkIsCompleteProfileScreen();

  if (user?.isClient()) {
    navList = clientLinks(user?.firmId);
    dropOptions = clientOptions;
  } else if (isCompleteProfileScreen) {
    navList = [];
    dropOptions = [];
  } else {
    navList = adminLinks;
    dropOptions = adminOptions;
  }

  const shouldShowNavBar = !user?.isClientMember();
  return (
    <>
      <Modal
        open={isOpen}
        setOpen={setIsOpen}
        title="Log Out"
        bodyText="Are you sure you want to log out?"
        confirmText="Log Out"
        cancelText="Cancel"
        swapButtons
        buttonAlignment="right"
        onConfirm={() => {
          setIsOpen(false);
          logout();
        }}
      />
      <NavWrapper>
        <Nav>
          <FlexStartContentContainer>
            <Link to="/">
              <Logo width="8.063rem" height="1.5rem" />
            </Link>
            <div style={{ marginRight: "2rem" }} />
            {shouldShowNavBar && (
              <DesktopOnly>
                {navList.map((nav, index) => {
                  return (
                    <LinkWrapper to={nav.path} key={`navbar-${index}`}>
                      {({ isActive = false }) => {
                        return (
                          <DisplayFlex>
                            <FocusOutlineButton
                              icon={true}
                              color={Theme.colors.primaries_000}
                              hoverColor={Theme.colors.primaries_neg100}
                              noText={true}
                              img={<nav.icon width={18} />}
                              buttonText={nav.text}
                              key={`navbar-inner-button-${index}`}
                              active={isActive}
                              style={{ minWidth: 120 }}
                            />
                            <LinkSpacing />
                          </DisplayFlex>
                        );
                      }}
                    </LinkWrapper>
                  );
                })}
              </DesktopOnly>
            )}
          </FlexStartContentContainer>
          <RightControls>
            {!isCompleteProfileScreen && (
              <DesktopOnly>
                <RelativeWrapper>
                  <div
                    onClick={() => setOpenDrop(!openDrop)}
                    onBlur={(e) => {
                      if (!e.relatedTarget?.id?.includes(`option_`))
                        setOpenDrop(false);
                    }}
                    tabIndex={0}
                  >
                    <InitialsAvatar
                      text={
                        user
                          ? `${user?.name
                              .split(" ")
                              .map((s) => s.charAt(0))
                              .join("")}`
                          : ""
                      }
                      background={Theme.colors.primaries_000}
                      hover={Theme.colors.primaries_neg100}
                      size={52}
                    />
                  </div>
                  {openDrop ? (
                    <AbsoluteWrapper>
                      <Sheet options={dropOptions} />
                    </AbsoluteWrapper>
                  ) : null}
                </RelativeWrapper>
              </DesktopOnly>
            )}
            {shouldShowNavBar && (
              <RelativeWrapper>
                <MobileOnly>
                  <button
                    aria-label="Open navigation"
                    onClick={() => {
                      if (!navMounted) {
                        setNavMounted(true);
                        requestAnimationFrame(() => setOpenNav(true));
                      } else {
                        setOpenNav((s) => !s);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".5rem",
                      padding: ".5rem .75rem",
                      borderRadius: ".75rem",
                      border: `2px solid ${Theme.colors.primaries_000}`,
                      background: "transparent",
                      color: Theme.colors.primaries_000,
                    }}
                  >
                    <MenuIcon width={20} />
                    <span style={{ fontWeight: 600 }}>Menu</span>
                  </button>
                  {navMounted && (
                    <>
                      <SheetBackdrop
                        className={openNav ? "open" : ""}
                        onClick={() => setOpenNav(false)}
                      />
                      <MobileMenuSheet
                        className={openNav ? "open" : ""}
                        onTransitionEnd={() => {
                          if (!openNav) setNavMounted(false);
                        }}
                      >
                        {navList.map((nav, index) => (
                          <LinkWrapper to={nav.path} key={`mnav-${index}`}>
                            {({ isActive = false }) => (
                              <div
                                style={{
                                  padding: ".25rem .5rem",
                                  display: "flex",
                                }}
                              >
                                <FocusOutlineButton
                                  icon={true}
                                  color={Theme.colors.primaries_000}
                                  hoverColor={Theme.colors.primaries_neg100}
                                  noText={true}
                                  img={<nav.icon width={16} />}
                                  buttonText={nav.text}
                                  key={`mnav-btn-${index}`}
                                  active={isActive}
                                  style={{
                                    width: "100%",
                                    justifyContent: "flex-start",
                                  }}
                                  onClick={() => setOpenNav(false)}
                                />
                              </div>
                            )}
                          </LinkWrapper>
                        ))}
                        {!isCompleteProfileScreen &&
                          dropOptions.map((option, index) => {
                            const getIcon = (text: string) => {
                              const isLogout = text
                                .toLowerCase()
                                .includes("logout");
                              const iconColor = isLogout
                                ? "#dc2626"
                                : undefined;

                              if (text.toLowerCase().includes("admin"))
                                return <CogIcon width={16} color={iconColor} />;
                              if (text.toLowerCase().includes("audit"))
                                return (
                                  <ClipboardListIcon
                                    width={16}
                                    color={iconColor}
                                  />
                                );
                              if (isLogout)
                                return (
                                  <LogoutIcon width={16} color={iconColor} />
                                );
                              if (
                                text.toLowerCase().includes("feedback") ||
                                text.toLowerCase().includes("question")
                              )
                                return (
                                  <UserIcon width={16} color={iconColor} />
                                );
                              return <UserIcon width={16} color={iconColor} />;
                            };

                            return (
                              <div
                                key={`mobile-drop-option-${index}`}
                                style={{
                                  padding: ".25rem .5rem",
                                  display: "flex",
                                }}
                              >
                                <FocusOutlineButton
                                  icon={true}
                                  color={
                                    option.text.toLowerCase().includes("logout")
                                      ? "#dc2626"
                                      : Theme.colors.primaries_000
                                  }
                                  hoverColor={
                                    option.text.toLowerCase().includes("logout")
                                      ? "#b91c1c"
                                      : Theme.colors.primaries_neg100
                                  }
                                  contentColor={
                                    option.text.toLowerCase().includes("logout")
                                      ? "#dc2626"
                                      : undefined
                                  }
                                  noText={true}
                                  img={getIcon(option.text)}
                                  buttonText={option.text}
                                  style={{
                                    width: "100%",
                                    justifyContent: "flex-start",
                                  }}
                                  onClick={() => {
                                    option.action();
                                    setOpenNav(false);
                                  }}
                                />
                              </div>
                            );
                          })}
                      </MobileMenuSheet>
                    </>
                  )}
                </MobileOnly>
              </RelativeWrapper>
            )}
          </RightControls>
        </Nav>
      </NavWrapper>
    </>
  );
};
